import React from "react";
import { Users, FolderKanban, Eye, ListTree, PenLine } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../../contexts/AuthContext";
import { SERVER_URL } from "../../../../utils/serverConfig";
import { useDashTheme } from "../../hooks/useDashTheme";
import { rangeForPreset } from "../../components/DateRange";
import { GRAPHICAL_BASE, personPath, projectPath } from "../../components/drilldown/drilldownRoutes";
import { GPanel, PanelHead } from "./parts";
import { RaceRows, ProjectDonut, inks, paper } from "./viz";
import { DrillFrame } from "./drill/DrillFrame";
import { BeatList, SkeletonRows, WaitLine } from "./drill/StoryChrome";
import { TimeRail, railFromSittings, fmt12 } from "./drill/TimelineKit";
import useEscapeBack from "./drill/useEscapeBack";

/**
 * Know your organization — the graphical redesign (ORG_TODAY_REDESIGN_MOCK.html,
 * user-approved 2026-08-23). Presentation layer ONLY: the SSE stream, its
 * event grammar and the page contract (auto-start, today-only, admin-only,
 * no date picker) are unchanged.
 *
 * The page's magic is that it is WRITTEN WHILE YOU READ, so the layout leads
 * with the writing: the first beat becomes the hero lede (with a caret while
 * the writer runs), the rest arrive as numbered story beats, and the numbers
 * the stream already carries become the same graphics the dashboard uses —
 * per-person day lanes, the project donut, people race rows, ink-railed
 * theme cards. Nothing here needs a byte more from the server.
 */

function isOrgLead(user) {
  return user && (user.role === "org_admin" || user.role === "main_admin");
}

/** Epoch ms → minutes since local midnight of that instant's own day. */
const minuteOfDay = (ms) => {
  const d = new Date(ms);
  return d.getHours() * 60 + d.getMinutes();
};

/** The one identity a sitting has on the lanes and the rail. */
const sittingId = (st) => `${st.userId}|${st.startMs}`;

const INK_CYCLE = ["violet", "blue", "gold", "pink"];

/** Index of the hovered row button inside a wrapped row list (event delegation). */
function rowIndexFromEvent(e, wrapEl) {
  const btn = e.target && e.target.closest ? e.target.closest("button") : null;
  if (!btn || !wrapEl || !wrapEl.contains(btn)) return -1;
  return Array.prototype.indexOf.call(wrapEl.querySelectorAll("button"), btn);
}

const bigHours = (min) => {
  const m = Math.max(0, Math.round(Number(min) || 0));
  if (m < 60) return { v: String(m), unit: "m" };
  // Hours stay the headline, but the minutes ride along in the unit so the
  // number is never rounded away (12h31m used to render as "13h").
  const mm = m % 60;
  return { v: String(Math.floor(m / 60)), unit: mm ? `h ${String(mm).padStart(2, "0")}m` : "h" };
};

/**
 * The day on a line — one lane per person, blocks are REAL fetched
 * start→end pairs. Grows live as sittings stream in (each block mounts with
 * the tgw-growX entrance). Cross-lit with the sittings rail below through
 * the shared sitting ids.
 */
function DayLanes({ t, people, sitRows, inkByUser, hotId, onHot, hotUser }) {
  const k = inks(t);
  const ppr = paper(t);

  const { lanes, lo, hi, rest } = React.useMemo(() => {
    const byUser = new Map();
    for (const st of sitRows || []) {
      if (!st || !Number.isFinite(st.startMs) || !Number.isFinite(st.endMs) || st.endMs <= st.startMs) continue;
      const uid = String(st.userId || "");
      if (!uid) continue;
      if (!byUser.has(uid)) byUser.set(uid, []);
      const s = minuteOfDay(st.startMs);
      let e = minuteOfDay(st.endMs);
      if (e <= s) e = Math.min(1440, s + Math.max(1, Math.round((st.endMs - st.startMs) / 60000)));
      byUser.get(uid).push({ id: sittingId(st), s, e });
    }
    const ordered = (people || []).filter((p) => byUser.has(String(p.userId)));
    const shown = ordered.slice(0, 6);
    const blocks = shown.flatMap((p) => byUser.get(String(p.userId)));
    if (!blocks.length) return { lanes: [], lo: 0, hi: 0, rest: 0 };
    let a = Math.min(...blocks.map((b) => b.s));
    let b = Math.max(...blocks.map((b) => b.e));
    a = Math.max(0, a - 20); b = Math.min(1440, b + 20);
    if (b - a < 180) { const mid = (a + b) / 2; a = Math.max(0, mid - 90); b = Math.min(1440, mid + 90); }
    return {
      lanes: shown.map((p) => ({
        userId: String(p.userId),
        name: String(p.name || "Someone").split(" ")[0],
        minutes: p.totalMinutes,
        blocks: byUser.get(String(p.userId)),
      })),
      lo: a, hi: b,
      rest: ordered.length - shown.length,
    };
  }, [people, sitRows]);

  if (!lanes.length) return null;
  const span = hi - lo || 1;
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(lo + span * f));
  const fmtM = (m) => (m >= 60 ? `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, "0")}m` : `${m}m`);

  return (
    <div>
      <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
        {lanes.map((ln) => {
          const ink = k[inkByUser[ln.userId] || "blue"];
          const dim = hotUser != null && hotUser !== ln.userId;
          return (
            <div
              key={ln.userId}
              style={{
                display: "grid", gridTemplateColumns: "64px 1fr 52px", gap: 10, alignItems: "center",
                opacity: dim ? 0.35 : 1, transition: "opacity .15s ease",
              }}
            >
              <span style={{
                fontSize: 11.5, fontWeight: 650, color: t.c.textSecondary, textAlign: "right",
                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
              }}>{ln.name}</span>
              <span style={{ position: "relative", height: 14, borderRadius: 7, background: ppr.well }}>
                {ln.blocks.map((b) => {
                  const hot = hotId != null && hotId === b.id;
                  return (
                    <i
                      key={b.id}
                      className="tgw-growX"
                      onMouseEnter={onHot ? () => onHot(b.id) : undefined}
                      onMouseLeave={onHot ? () => onHot(null) : undefined}
                      title={`${ln.name} · ${fmt12(b.s)} – ${fmt12(b.e)}`}
                      style={{
                        position: "absolute", top: 2, bottom: 2, borderRadius: 5, background: ink,
                        left: `${((b.s - lo) / span) * 100}%`,
                        width: `${Math.max(1.2, ((b.e - b.s) / span) * 100)}%`,
                        boxShadow: hot ? `0 0 0 2px ${ppr.card}, 0 0 0 3.5px ${ink}` : "none",
                        cursor: "default",
                      }}
                    />
                  );
                })}
              </span>
              <span style={{ fontSize: 11, fontWeight: 700, color: t.c.textSecondary, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                {fmtM(ln.minutes)}
              </span>
            </div>
          );
        })}
      </div>
      <div style={{
        display: "flex", justifyContent: "space-between", margin: "6px 0 0 74px", paddingRight: 62,
        fontSize: 9.5, color: t.c.textTertiary, fontVariantNumeric: "tabular-nums",
      }}>
        {ticks.map((m, i) => <span key={i}>{fmt12(m)}</span>)}
      </div>
      {rest > 0 ? (
        <div style={{ fontSize: 11, color: t.c.textTertiary, marginTop: 8 }}>
          +{rest} more {rest === 1 ? "person" : "people"} in the list below
        </div>
      ) : null}
    </div>
  );
}

/** Ink-railed theme cards — what got done, each opening its project. */
function ThemeCards({ t, themes, onOpen }) {
  const k = inks(t);
  const ppr = paper(t);
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12 }}>
      {themes.map((th, i) => {
        const ink = k[INK_CYCLE[i % INK_CYCLE.length]];
        const opens = Boolean(th.projectId);
        return (
          <button
            key={th.key || i}
            type="button"
            onClick={opens ? () => onOpen(th) : undefined}
            className="tgw-card"
            style={{
              position: "relative", overflow: "hidden", textAlign: "left",
              background: ppr.card, border: `1px solid ${t.c.border}`, borderRadius: t.r.card,
              padding: "15px 17px 13px 19px", fontFamily: "inherit", color: "inherit",
              cursor: opens ? "pointer" : "default",
              boxShadow: t.shadow.raised,
            }}
          >
            <i aria-hidden="true" style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 4, background: ink }} />
            <span style={{ display: "flex", justifyContent: "space-between", gap: 8, fontSize: 14.5, fontWeight: 700, letterSpacing: "-0.2px" }}>
              <span style={{ minWidth: 0 }}>{th.title}</span>
              {opens ? <span style={{ color: k.blue, fontWeight: 800, fontSize: 12, flexShrink: 0 }}>›</span> : null}
            </span>
            {th.what ? (
              <span style={{ display: "block", fontSize: 12.5, color: t.c.textSecondary, lineHeight: 1.55, marginTop: 6 }}>{th.what}</span>
            ) : null}
            {th.who?.length ? (
              <span style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 11 }}>
                {th.who.map((w) => (
                  <span key={w} style={{
                    fontSize: 11, fontWeight: 650, padding: "3px 9px", borderRadius: 99,
                    background: ppr.well, color: t.c.textSecondary,
                  }}>{w}</span>
                ))}
              </span>
            ) : null}
            {th.projectName ? (
              <span style={{ display: "block", marginTop: 10, fontSize: 11, fontWeight: 700, color: ink }}>{th.projectName}</span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

export default function OrgToday() {
  const t = useDashTheme();
  const navigate = useNavigate();
  const { user } = useAuth();
  const range = React.useMemo(() => rangeForPreset("today"), []);
  const allowed = isOrgLead(user);
  const k = inks(t);
  const ppr = paper(t);

  const [beats, setBeats] = React.useState([]);
  const [themes, setThemes] = React.useState([]);
  const [looks, setLooks] = React.useState([]);
  const [sittings, setSittings] = React.useState([]);
  const [facts, setFacts] = React.useState(null);
  const [empty, setEmpty] = React.useState("");
  const [error, setError] = React.useState("");
  const [running, setRunning] = React.useState(!!allowed);
  const [hotId, setHotId] = React.useState(null);
  const [hotUser, setHotUser] = React.useState(null);
  const peopleWrapRef = React.useRef(null);

  useEscapeBack(React.useCallback(() => navigate(GRAPHICAL_BASE), [navigate]));

  React.useEffect(() => {
    if (!allowed) return undefined;
    const ctrl = new AbortController();
    setRunning(true);
    (async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(`${SERVER_URL}/api/v1/classic-dashboard/org-today`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({}),
          signal: ctrl.signal,
        });
        if (res.status === 403) throw new Error("This picture is for organisation admins.");
        if (!res.ok) throw new Error("Could not write today.");
        const reader = res.body.getReader();
        const dec = new TextDecoder();
        let buf = "";
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += dec.decode(value, { stream: true });
          const lines = buf.split("\n");
          buf = lines.pop() || "";
          for (const line of lines) {
            if (!line.startsWith("data: ")) continue;
            let d; try { d = JSON.parse(line.slice(6)); } catch (_e) { continue; }
            if (d.type === "facts") setFacts(d.facts);
            else if (d.type === "beat") setBeats((c) => [...c, d.text]);
            else if (d.type === "theme") setThemes((c) => [...c, d]);
            else if (d.type === "look") setLooks((c) => [...c, d.text]);
            else if (d.type === "sitting") setSittings((c) => [...c, d]);
            else if (d.type === "empty") setEmpty(d.message);
            else if (d.type === "error") setError(d.message);
            else if (d.type === "done") setRunning(false);
          }
        }
      } catch (e) {
        if (e.name !== "AbortError") setError(e.message);
      } finally { setRunning(false); }
    })();
    return () => ctrl.abort();
  }, [allowed]);

  const people = (facts?.people || []).map((p) => ({
    userId: p.userId,
    name: p.name,
    totalMinutes: p.minutes,
    activeMinutes: p.minutes,
    idleMinutes: 0,
  }));
  const sitRows = React.useMemo(() => {
    const base = sittings.length ? sittings : (facts?.sittings || []);
    return [...base].sort((a, b) => (Number(a.startMs) || 0) - (Number(b.startMs) || 0));
  }, [sittings, facts]);
  const weekday = new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });

  /* One ink per person, assigned in carried-most-first order, shared by the
     hero lanes and the sittings rail so a person keeps one colour everywhere. */
  const inkByUser = React.useMemo(() => {
    const map = {};
    people.forEach((p, i) => { map[String(p.userId)] = INK_CYCLE[i % INK_CYCLE.length]; });
    return map;
  }, [people]);
  const railInkById = React.useMemo(() => {
    const map = {};
    for (const st of sitRows) map[sittingId(st)] = inkByUser[String(st.userId)] || "blue";
    return map;
  }, [sitRows, inkByUser]);

  const hoverPerson = (e) => {
    const i = rowIndexFromEvent(e, peopleWrapRef.current);
    setHotUser(i >= 0 && people[i] ? String(people[i].userId) : null);
  };

  const totalMin = people.reduce((a, p) => a + (Number(p.totalMinutes) || 0), 0);
  const hrs = bigHours(totalMin);
  const lede = beats[0] || null;
  const restBeats = beats.slice(1);
  const projects = facts?.projects || [];
  const shVars = { "--tgw-sh0": ppr.well, "--tgw-sh1": t.c.primarySoft || t.c.border };

  const heroKpis = [
    { v: facts ? hrs.v : "…", u: facts ? hrs.unit : "", l: "readable time" },
    { v: people.length ? String(people.length) : (running ? "…" : "—"), u: "", l: "people" },
    { v: projects.length ? String(projects.length) : (running ? "…" : "—"), u: "", l: "projects" },
    { v: facts?.windowCount || sitRows.length || (running ? "…" : "—"), u: "", l: "sittings" },
  ];

  return (
    <DrillFrame
      t={t}
      eyebrow="Today only"
      title="Know your organization"
      subtitle={weekday}
      backTo={GRAPHICAL_BASE}
      backLabel="Dashboard"
      crumbs={[{ label: "Dashboard", to: GRAPHICAL_BASE }, { label: "Today" }]}
    >
      {!allowed ? (
        <GPanel t={t}>This picture is for organisation admins.</GPanel>
      ) : (
        <>
          {/* caret for the live lede — local, tiny, honest: it blinks only
              while the writer is actually running */}
          <style>{`@keyframes tgo-blink { 50% { opacity: 0; } }`}</style>

          {/* ── the story hero — violet is the writing ink ─────────────── */}
          <div
            className="tgw-a tgw-a1"
            style={{
              display: "grid", gridTemplateColumns: "1.05fr .95fr", borderRadius: t.r.hero,
              border: `1px solid ${t.c.border}`, marginBottom: 12, overflow: "hidden",
              background: `linear-gradient(135deg, ${k.violet}17, ${ppr.card} 55%)`,
              boxShadow: `0 1px 1px ${t.c.shadowColor}, 0 22px 48px -26px ${t.c.shadowColorStrong}`,
            }}
          >
            <div style={{ padding: "24px 26px 20px", minWidth: 0 }}>
              <div style={{
                ...t.font.statLabel,
                color: k.violet, display: "flex", alignItems: "center", gap: 8,
              }}>
                <i className="tgw-live" style={{ background: k.violet }} />
                {running ? "Being written now" : "Today, in one line"}
              </div>

              {error && !lede ? (
                <div style={{ marginTop: 14, fontSize: 15, color: t.status?.negative || t.c.textSecondary }}>{error}</div>
              ) : empty && !lede ? (
                <div style={{ marginTop: 14, fontSize: 16, lineHeight: 1.5, color: t.c.textSecondary, maxWidth: "42ch" }}>{empty}</div>
              ) : lede ? (
                <div style={{
                  margin: "12px 0 0", fontSize: 23, fontWeight: 650, letterSpacing: "-0.6px",
                  lineHeight: 1.35, maxWidth: "34ch",
                }}>
                  {lede}
                  {running ? (
                    <i aria-hidden="true" style={{
                      display: "inline-block", width: 2, height: "1em", background: k.violet,
                      verticalAlign: -2, marginLeft: 3, animation: "tgo-blink 1s steps(1) infinite",
                    }} />
                  ) : null}
                </div>
              ) : (
                <div style={{ marginTop: 14 }}>
                  <WaitLine t={t} text="Reading the day…" />
                  <SkeletonRows count={2} height={22} />
                </div>
              )}

              <div style={{ display: "flex", gap: 26, flexWrap: "wrap", marginTop: 20 }}>
                {heroKpis.map((x) => (
                  <div key={x.l}>
                    <div style={{ fontSize: 25, fontWeight: 720, letterSpacing: "-1px", fontVariantNumeric: "tabular-nums" }}>
                      {x.v}<i style={{ fontStyle: "normal", fontSize: 14, color: t.c.textTertiary, fontWeight: 650 }}>{x.u}</i>
                    </div>
                    <div style={{ ...t.font.microLabel, color: t.c.textTertiary, marginTop: 2 }}>
                      {x.l}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{
              padding: "18px 20px 14px", borderLeft: `1px solid ${t.c.border}`,
              background: `${ppr.well}8c`, minWidth: 0,
            }}>
              <div style={{ fontSize: 12.5, fontWeight: 700 }}>The day on a line</div>
              <div style={{ fontSize: 11, color: t.c.textTertiary, margin: "2px 0 12px" }}>
                one lane per person · real start → end blocks{running ? " · growing as the writer reads" : ""}
              </div>
              {sitRows.length ? (
                <DayLanes
                  t={t}
                  people={people}
                  sitRows={sitRows}
                  inkByUser={inkByUser}
                  hotId={hotId}
                  onHot={setHotId}
                  hotUser={hotUser}
                />
              ) : running ? (
                <SkeletonRows count={4} height={14} />
              ) : (
                <div style={{ fontSize: 12, color: t.c.textTertiary }}>No timed sittings to draw yet.</div>
              )}
              {running ? (
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 14, fontSize: 11.5, color: t.c.textTertiary }}>
                  <i className="tgw-live" style={{ background: k.violet }} />
                  still writing
                  <span className="tgw-shimmer" style={{ ...shVars, flex: 1, height: 8, borderRadius: 99 }} />
                </div>
              ) : null}
            </div>
          </div>

          {/* ── the story — beats after the lede, numbered, as they arrive ── */}
          {(restBeats.length || (running && lede)) ? (
            <GPanel t={t} className="tgw-card tgw-a tgw-a2" style={{ marginBottom: 12 }}>
              <PanelHead
                t={t}
                title="The story of today"
                icon={<PenLine size={15} strokeWidth={2.2} />}
                tone="violet"
                subtitle={running ? "each line arrives as it is written" : "what the team actually did"}
                info="focus"
                cue={running ? <i className="tgw-live" style={{ background: k.violet }} /> : (restBeats.length || null)}
              />
              <BeatList t={t} beats={restBeats} running={running && !empty && !error} />
            </GPanel>
          ) : null}

          {/* ── the hours donut + the people ───────────────────────────── */}
          <div className="tgw-pair tgw-a tgw-a3" style={{ display: "grid", gridTemplateColumns: "1fr 1.15fr", gap: 12, marginBottom: 12 }}>
            <GPanel t={t} className="tgw-card">
              <PanelHead
                t={t}
                title="Where the hours pooled"
                icon={<FolderKanban size={15} strokeWidth={2.2} />}
                tone="gold"
                subtitle="projects, by minutes"
                info="projectShare"
                cue={projects.length ? "click a project →" : null}
              />
              {projects.length ? (
                <ProjectDonut
                  t={t}
                  projects={projects}
                  onProject={(p) => p?.projectId && navigate(projectPath(p.projectId, range, null, p.name))}
                />
              ) : running ? (
                <SkeletonRows count={4} />
              ) : (
                <div style={{ fontSize: 13, color: t.c.textSecondary }}>No project carried readable time today.</div>
              )}
            </GPanel>

            <GPanel t={t} className="tgw-card">
              <PanelHead
                t={t}
                title="Team Activity Log"
                icon={<Users size={15} strokeWidth={2.2} />}
                tone="blue"
                subtitle="readable time per person · open one"
                info="people"
                cue={people.length || null}
              />
              {running && !people.length ? <SkeletonRows count={3} /> : null}
              {people.length ? (
                <div
                  ref={peopleWrapRef}
                  onMouseOver={hoverPerson}
                  onMouseOut={() => setHotUser(null)}
                >
                  <RaceRows
                    t={t}
                    people={people}
                    onPerson={(p) => navigate(personPath(p.userId, range, null, p.name))}
                  />
                </div>
              ) : (!running ? (
                <div style={{ fontSize: 13.5, color: t.c.textSecondary }}>No one has readable time yet today.</div>
              ) : null)}
            </GPanel>
          </div>

          {/* ── pieces of work — ink-railed cards, each opens its project ── */}
          {(themes.length || running) ? (
            <GPanel t={t} className="tgw-card tgw-a tgw-a4" style={{ marginBottom: 12 }}>
              <PanelHead
                t={t}
                title="Pieces of work"
                icon={<ListTree size={15} strokeWidth={2.2} />}
                tone="violet"
                subtitle="what got done, grouped · open a project"
                info="focus"
                cue={themes.length || null}
              />
              {running && !themes.length ? (
                <>
                  <WaitLine t={t} text="Sorting today’s work…" />
                  <SkeletonRows count={2} height={56} />
                </>
              ) : null}
              {themes.length ? (
                <ThemeCards
                  t={t}
                  themes={themes}
                  onOpen={(th) => navigate(projectPath(th.projectId, range, null, th.projectName || th.title))}
                />
              ) : (!running ? (
                <div style={{ fontSize: 13.5, color: t.c.textSecondary }}>No distinct pieces of work to open yet.</div>
              ) : null)}
            </GPanel>
          ) : null}

          {/* ── worth a look ───────────────────────────────────────────── */}
          {(looks.length || running) ? (
            <GPanel t={t} className="tgw-card tgw-a" style={{ marginBottom: 12 }}>
              <PanelHead
                t={t}
                title="Worth a look"
                icon={<Eye size={15} strokeWidth={2.2} />}
                tone="pink"
                subtitle="the writer flags these — you decide"
                info="attention"
                cue={looks.length || null}
              />
              {looks.length ? (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: 10 }}>
                  {looks.map((tx, i) => (
                    <div key={i} className="tgw-a" style={{
                      display: "flex", gap: 11, alignItems: "flex-start",
                      padding: "12px 14px", borderRadius: t.r.well, background: ppr.well,
                    }}>
                      <i aria-hidden="true" style={{ width: 9, height: 9, borderRadius: 99, background: k.violet, marginTop: 5, flexShrink: 0 }} />
                      <span style={{ fontSize: 13, lineHeight: 1.55, color: t.c.textSecondary }}>{tx}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <SkeletonRows count={2} />
              )}
            </GPanel>
          ) : null}

          {/* ── the sittings — the TimeRail, person-inked ──────────────── */}
          {(sitRows.length || running) ? (
            <GPanel t={t} className="tgw-card tgw-a">
              <PanelHead
                t={t}
                title="The sittings"
                subtitle="who sat where, on the clock · each opens the person"
                info="focus"
                cue={sitRows.length || null}
              />
              {running && !sitRows.length ? <SkeletonRows count={3} /> : null}
              {sitRows.length ? (
                /* Same ids as the hero lanes, so hovering a card rings its
                   block up there and back. Gaps off: a hole between two
                   different people's sittings says nothing about the org. */
                <TimeRail
                  t={t}
                  hotId={hotId}
                  onHot={setHotId}
                  onOpen={(row) => row.raw?.userId && navigate(personPath(row.raw.userId, range, null, row.raw.name))}
                  rows={railFromSittings(sitRows, { inkMap: railInkById, idFor: (st) => sittingId(st) })}
                  gaps={false}
                />
              ) : null}
            </GPanel>
          ) : null}

          <div style={{ marginTop: 18, textAlign: "center", fontSize: 11.5, color: t.c.textTertiary, lineHeight: 1.7 }}>
            Today only · written from real activity while you read · nothing invented, absences stated.
            <br />Every number, name and sitting is a door.
          </div>
        </>
      )}
    </DrillFrame>
  );
}
